/**
 * Backfills the preview URL for placed photos that only carry an image id — chiefly images
 * loaded from legacy NSD (`<IM II="..." IV="..." />` never stored a preview URL) or the pre-fix
 * bare `imageElement` format, neither of which persisted one. Applies to both a `photoFill`
 * (the normalized representation for both standalone drops and shapes filled with a photo — see
 * build-placed-photo-shape.ts) and, defensively, the legacy top-level `imageId`/`imagePreviewUrl`
 * fields directly. Resolves via photosService.getCandidDetail and, once resolved, persists it
 * through the normal `onShapeCommitted` path so future loads don't need to re-resolve.
 *
 * A ClikArt fill (art id in `pezId`, no preview) is backfilled the same way, from its art id: a
 * ClikArt authored in the legacy app, or one a template placed before its lookup succeeded, has no
 * preview URL either (see lib/clikart/resolve-clikart-preview).
 *
 * See lib/photos/resolve-missing-image-preview.ts for the pure predicate/patch logic.
 */
import * as React from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { clikArtService } from '@/api/services/clikart.service'
import { photosService } from '@/api/services/photos.service'
import {
  clikArtPreviewPatch,
  shapeNeedsClikArtPreview,
} from '@/lib/clikart/resolve-clikart-preview'
import { resolvePlacedClikArtId } from '@/lib/clikart/sync-clikart-placement-from-shapes'
import {
  imageIdNeedingResolution,
  imagePreviewPatchFromCandidDetail,
  shapeNeedsImagePreviewResolution,
} from '@/lib/photos/resolve-missing-image-preview'
import { logger } from '@/utils/logger'

export type UseResolveMissingImagePreviewsArgs = {
  spreadId: string
  /** The freshly loaded (or synced) shapes to scan — a snapshot is enough, not a live ref. */
  shapes: ShapeData[] | undefined
  shapeManagerRef: React.RefObject<ShapeManagerRef | null>
  onShapeCommitted: (shape: ShapeData, source?: 'update') => void
}

export function useResolveMissingImagePreviews({
  spreadId,
  shapes,
  shapeManagerRef,
  onShapeCommitted,
}: UseResolveMissingImagePreviewsArgs): void {
  const attemptedRef = React.useRef<Set<string>>(new Set())
  const spreadIdRef = React.useRef(spreadId)

  if (spreadIdRef.current !== spreadId) {
    spreadIdRef.current = spreadId
    attemptedRef.current = new Set()
  }

  React.useEffect(() => {
    const candidates = (shapes ?? []).filter(
      (shape) =>
        (shapeNeedsImagePreviewResolution(shape) || shapeNeedsClikArtPreview(shape)) &&
        !attemptedRef.current.has(shape.id)
    )

    // Patches the shape in the manager once its lookup is back (if it is still there and still
    // missing its preview), then commits it so the URL is saved and future loads skip the lookup.
    const apply = (
      objectId: string,
      stillNeeds: (shape: ShapeData) => boolean,
      patchFor: (shape: ShapeData) => Partial<ShapeData> | null
    ) => {
      const mgr = shapeManagerRef.current
      const current = mgr?.getShapes().find((s) => s.id === objectId)

      if (!mgr || !current || !stillNeeds(current)) return

      const patch = patchFor(current)

      if (!patch) return

      mgr.updateShape(objectId, patch, { bypassEditGuards: true })

      const updated = mgr.getShapes().find((s) => s.id === objectId)

      if (updated) onShapeCommitted(updated, 'update')
    }

    for (const shape of candidates) {
      attemptedRef.current.add(shape.id)

      const objectId = shape.id

      if (shapeNeedsClikArtPreview(shape)) {
        void clikArtService
          .getClikArtById(resolvePlacedClikArtId(shape) as string)
          .then((item) => {
            if (item) {
              apply(objectId, shapeNeedsClikArtPreview, (s) => clikArtPreviewPatch(s, item))
            }
          })
          .catch((err) => {
            logger.warn('Failed to resolve preview URL for placed ClikArt:', err)
          })

        continue
      }

      const imageId = imageIdNeedingResolution(shape) as string

      void photosService
        .getCandidDetail(imageId)
        .then((detail) => {
          apply(objectId, shapeNeedsImagePreviewResolution, (s) =>
            imagePreviewPatchFromCandidDetail(s, detail)
          )
        })
        .catch((err) => {
          logger.warn('Failed to resolve preview URL for placed photo:', err)
        })
    }
  }, [shapes, shapeManagerRef, onShapeCommitted])
}
